// Humanisation d'affichage des étiquettes de locuteur non résolues.
//
// Les étiquettes brutes (`LOC_n`, `SPEAKER_n`, `Locuteur n` — voir
// `supabase/functions/_shared/pseudonymize.ts` LABEL_LINE_RE/LABEL_INLINE_RE)
// sont techniques et illisibles pour un usager. Tant qu'aucune personne n'a
// été attribuée (annuaire de locuteurs), on les affiche « Personne N » plutôt
// que le token brut.
//
// Affichage SEULEMENT : ne touche jamais aux données stockées (transcript,
// items, synthèse) ni aux clés d'attribution (celles-ci continuent d'utiliser
// l'étiquette brute — voir `capture_speaker_persons`).
const SPEAKER_LABEL_RE = /^(?:LOC_|SPEAKER_)(\d+)$|^Locuteur\s+(\d+)$/i;

export function humanizeSpeakerLabel(label: string): string {
  const trimmed = label.trim();
  const match = SPEAKER_LABEL_RE.exec(trimmed);
  if (!match) return label;
  const n = match[1] ?? match[2];
  return `Personne ${Number(n)}`;
}

// Une étiquette brute peut aussi se retrouver DANS un texte, pas seulement dans
// un champ `speaker` : la pseudonymisation remplace les noms réels par `LOC_n`
// à même le transcript (`applyPseudonyms`), et le LLM reprend ensuite le token
// dans sa prose — « LOC_1 prévoit passer installer l'application chez LOC_2. »
// (constaté en prod le 25-08 : 106 des 811 `capture_items.summary`, 7 des 91
// `capture_syntheses.executive_summary`). C'est la même convention d'affichage
// qui s'applique : nom résolu de l'annuaire, sinon « Personne N ».
//
// Restreint volontairement à `LOC_n` / `SPEAKER_n` : ces deux formes sont des
// tokens techniques, jamais du français, donc zéro faux positif. « Locuteur 2 »
// se lit déjà en prose et son remplacement casserait la phrase (« le Personne 2
// hésite ») — même prudence que LABEL_INLINE_RE côté edge.
const SPEAKER_LABEL_IN_TEXT_RE = /\b(?:LOC|SPEAKER)_(\d+)\b/gi;

export function humanizeSpeakerLabelsInText(
  text: string,
  speakerNames?: ReadonlyMap<string, string>,
): string {
  return text.replace(SPEAKER_LABEL_IN_TEXT_RE, (raw) => {
    const resolved = speakerNames?.get(raw);
    return resolved ?? humanizeSpeakerLabel(raw);
  });
}
